import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { API_CONFIG, buildApiUrl } from '../config/api';

export default function ReportsPartsPerAparat() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const token = localStorage.getItem('token');
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const url = buildApiUrl(API_CONFIG.ENDPOINTS.REPORTS.PARTS_PER_APARAT, {
          page: String(page),
          pageSize: String(pageSize),
          ...(q.trim() && { q: q.trim() })
        });
        const resp = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!resp.ok) throw new Error('Greška pri učitavanju izvještaja.');
        const data = await resp.json();
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(Number(data.total || 0));
        setTotalPages(Number(data.totalPages || 1));
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [page, pageSize, q]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter(r =>
      (r.naziv_aparata || '').toLowerCase().includes(term) ||
      (r.naziv_dijela || '').toLowerCase().includes(term)
    );
  }, [items, q]);

  const handleDownload = async (format) => {
    const token = localStorage.getItem('token');
    const url = buildApiUrl(API_CONFIG.ENDPOINTS.REPORTS.PARTS_PER_APARAT, {
      format,
      ...(q.trim() && { q: q.trim() })
    });
    try {
      const resp = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Neuspješno preuzimanje.');
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = format === 'pdf' ? 'utroseni-dijelovi-po-aparatima.pdf' : 'utroseni-dijelovi-po-aparatima.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      alert(e.message);
    }
  };

  // Ne prekidamo render tokom učitavanja da fokus na inputu ostane

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Utrošeni dijelovi po aparatima</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => handleDownload('pdf')} className="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700" title="Preuzmi PDF">
            <FontAwesomeIcon icon={faFilePdf} /> PDF
          </button>
          <button onClick={() => handleDownload('xlsx')} className="px-3 py-2 rounded-md bg-green-600 text-white hover:bg-green-700" title="Preuzmi Excel">
            <FontAwesomeIcon icon={faFileExcel} /> Excel
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-xs text-gray-500">Filter za prilagođene izvještaje</span>
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Pretraga po aparatu ili dijelu..."
            className="w-full sm:w-96 px-3 py-2 border border-gray-300 rounded-md"
          />
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Redova po stranici:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="px-2 py-2 border border-gray-300 rounded-md"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-600">Ukupno: {total}</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aparat</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dio</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Količina</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.map((row, idx) => (
              <tr key={`${row.id_aparat}-${row.naziv_dijela}-${idx}`} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{row.naziv_aparata || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{row.naziv_dijela || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right">{row.total_kolicina}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-center text-gray-500">Nema podataka.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">Stranica {page} od {totalPages}</div>
        <div className="space-x-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className={`px-3 py-2 rounded-md border ${page <= 1 ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
          >
            Prethodna
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className={`px-3 py-2 rounded-md border ${page >= totalPages ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
          >
            Sljedeća
          </button>
        </div>
      </div>
    </div>
  );
}
